import Head from 'next/head';
import type { GetServerSideProps, InferGetServerSidePropsType } from 'next';

import { NextPageWithLayout } from '@/pages/_app';
import React, { useMemo } from 'react';
import Layout from '@/index-layouts';
import { AppSettings } from '@/shared/app-common';
import dynamic from 'next/dynamic';
import { getGuideDetailListApi } from '@/lib/service/home';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
const GuideContent = dynamic(() => import('@/components/GuideList'), {
  ssr: true,
});
import { getTdkApi } from '@/lib/service/tdk';
import { page2JsonLd } from '@/lib/common/jsonld';
import { hyphenatedToTitleCase } from '@/shared/path-helper';
export const getServerSideProps: GetServerSideProps = async (context) => {
  context.res.setHeader('Cache-Control', 'public, s-maxage=10, stale-while-revalidate=59');
  const { locale = 'en', resolvedUrl, req } = context;

  const [localeRes, tdkData, res, translations] = await Promise.all([
    getGuideDetailListApi(context.locale),
    getTdkApi(context.resolvedUrl, context.locale),
    getGuideDetailListApi(locale),
    serverSideTranslations(locale, ['common']),
  ]);
  let currentURL = `https://${req.headers.host}${resolvedUrl}`;
  if (locale != 'en') {
    currentURL = `${process.env.NEXT_APP_WEB_HOST}/${locale}/guide`;
  }
  const siteUrl =
    locale !== 'en' ? `https://${process.env.NEXT_APP_WEB_HOST}/${locale}` : `https://${process.env.NEXT_APP_WEB_HOST}`;
  const pageTitle = tdkData?.data?.title ?? null;
  const pageDescription = tdkData?.data?.description ?? null;
  const pageKeywrods = tdkData?.data?.keywords ?? null;
  const jsonLd = page2JsonLd(
    {
      title: hyphenatedToTitleCase(resolvedUrl),
      desc: pageDescription,
      url: currentURL,
    },
    { url: siteUrl },
  );

  return {
    props: {
      ...localeRes,
      ...translations,
      herf: currentURL,
      locale,
      data: res?.data,
      title: pageTitle,
      description: pageDescription,
      keywords: pageKeywrods,
      content: 'guide',
      jsonLd: jsonLd,
    },
  };
};

const Home: NextPageWithLayout<InferGetServerSidePropsType<typeof getServerSideProps>> = (props: any) => {
  return useMemo(() => <GuideContent data={props.data} />, [props]);
};
Home.getLayout = function getLayout(page: React.ReactElement) {
  const title = page?.props?.title;
  const desc = page?.props?.description;
  const keywords = page?.props?.keywords;
  const jsonLd = page?.props?.jsonLd;

  return (
    <Layout
      head={
        <Head>
          <title>{title}</title>
          <meta name="title" content={title} />
          <meta name="description" content={desc} />
          <meta name="keywords" content={keywords} />

          <meta property="og:title" content={title} />
          <meta property="og:description" content={desc} />
          <meta
            property="og:image"
            content={'https://res-front.pumpsoul.com/prod-erasa/image/dmca-protect/dmca-5.png'}
          />
          <meta
            property="og:image:secure_url"
            content={'https://res-front.pumpsoul.com/prod-erasa/image/dmca-protect/dmca-5.png'}
          />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={page.props?.herf} />

          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={desc} />
          <meta
            name="twitter:image"
            content={'https://res-front.pumpsoul.com/prod-erasa/image/dmca-protect/dmca-5.png'}
          />
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:site" content={page.props?.herf} />

          <meta
            name="viewport"
            content="width=device-width, initial-scale=1, minimum-scale=1, maximum-scale=1, shrink-to-fit=no, user-scalable=no, viewport-fit=cover"
          />
          <link rel="alternate" hrefLang="en" href={`${process.env.NEXT_APP_WEB_HOST}/en/${page.props.content}`} />
          <link rel="alternate" hrefLang="zh" href={`${process.env.NEXT_APP_WEB_HOST}/zh/${page.props.content}`} />
          <link rel="alternate" hrefLang="tw" href={`${process.env.NEXT_APP_WEB_HOST}/tw/${page.props.content}`} />
          <link rel="alternate" hrefLang="ja" href={`${process.env.NEXT_APP_WEB_HOST}/ja/${page.props.content}`} />
          <link rel="alternate" hrefLang="ko" href={`${process.env.NEXT_APP_WEB_HOST}/ko/${page.props.content}`} />
          <link rel="alternate" hrefLang="pt" href={`${process.env.NEXT_APP_WEB_HOST}/pt/${page.props.content}`} />
          <link rel="alternate" hrefLang="de" href={`${process.env.NEXT_APP_WEB_HOST}/de/${page.props.content}`} />
          <link rel="alternate" hrefLang="es" href={`${process.env.NEXT_APP_WEB_HOST}/es/${page.props.content}`} />
          <link rel="alternate" hrefLang="it" href={`${process.env.NEXT_APP_WEB_HOST}/it/${page.props.content}`} />
          <link rel="alternate" hrefLang="x-default" href={`${process.env.NEXT_APP_WEB_HOST}/${page.props.content}`} />

          <link rel="canonical" href={page.props?.herf} />
          <link rel="icon" href={AppSettings.icoPath} />
          {jsonLd && (
            <script async type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
          )}
        </Head>
      }
    >
      {page}
    </Layout>
  );
};

export default Home;
